import { Stack, Typography } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { AppContext } from "../../../lib/Context";
import { emailRegex } from "../../../lib/Variable";
import { setPhoneValidation } from "../../../lib/redux/slices/phoneValidationSlice";
import { setSignedDate } from "../../../lib/redux/slices/signedDateSlice";
import { createContract } from "../../../services/ContractApi";
import {
  areAllKeysFilled,
  checkSpeedAvailability,
  handleOpenSnackBar,
  hasErrorInLineValues,
  selectedContractLength,
} from "../../../utils/helper";
import GlobalButton from "./../../../components/button/index";
import AppHeader from "./../../../components/header/index";
import Cli from "./Cli";
import ContractLength from "./ContractLength";
import CordedPhone from "./CordedPhone";
import CordedlessPhone from "./CordedlessPhone";
import DebitDetails from "./DebitDetails";
import Download from "./Download";
import Equipments from "./Equipments";
import GeneralConnections from "./GeneralConnections";
import Note from "./Note";
import PaperBillingCharges from "./PaperBillingCharges";
import Routers from "./Routers";
import SameConnections from "./SameConnections";
import SignedDate from "./SignedDate";
import Upload from "./Upload";
import Discount from "./Discount";
import { getAdminAgentDetails } from "../../../services/AgentApi";

const AddNewConnectionsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [adminDetails, setAdminDetail] = useState(null);
  const { state: routerState } = useLocation();
  const {
    note,
    cliData,
    generalInfo,
    contractLength,
    paperBillingPrice,
    isReqDebitDetail,
    calculateGrossProfit,
    calculateOneOfCost,
    calculateTotalRevenue,
    calculateOneOfCostProfit,
    isBillingEmailSame,
    equipments,
    eBillingAddress,
    debitDetails,
    routerValue,
    cordedPhone,
    cordedlessPhone,
    downloadSpeed,
    uploadSpeed,
    allNotes,
    contractEndDate,
    followUpDate,
    isUpfrontPaymentCollected,
    userData,
    isBankDetailsVerified,
    handleResetData,
    setShowAllContractLengths,
  } = useContext(AppContext);

  // Static variables
  const isAdmin = userData?.designation === "Admin" ? true : false;
  const totalOneOfCost = calculateOneOfCost();
  const totalRevenue = calculateTotalRevenue();
  const totalGrossProfit =
    parseFloat(calculateGrossProfit()) + parseFloat(calculateOneOfCostProfit());

  const { isDownloadSpeed, isUploadSpeed } = checkSpeedAvailability(cliData);

  // Redux state
  const { phoneValidation } = useSelector(
    (state) => state.phoneValidationReducer,
  );
  const { signedDate } = useSelector((state) => state.signedDateReducer);
  const { discount, discountDetails } = useSelector(
    (state) => state.discountReducer,
  );

  useEffect(() => {
    if (userData?._id) {
      fetchAdminDetail(userData?._id);
    }
  }, [userData]);

  // New lead form: never show deprecated 12/18 Months
  useEffect(() => {
    setShowAllContractLengths(false);
  }, [setShowAllContractLengths]);

  const fetchAdminDetail = async (id) => {
    const fields = "canPreview";
    try {
      const response = await getAdminAgentDetails(id, fields);
      if (response.data.status === "success") {
        const data = response.data.data;
        setAdminDetail(data);
      }
    } catch (error) {}
  };

  const handlePreviewContrat = async (isPreview) => {
    // Check all general information fields have values except signupDate and eBilling
    try {
      const isAllGenInfoFilled = areAllKeysFilled(generalInfo, [
        "signupDate",
        "eBilling",
        "address2",
        "town",
        "secondName",
        "isAddCustomer",
        "liveDate",
      ]);

      if (!isAllGenInfoFilled.allFilled) {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
        return handleOpenSnackBar(
          "error",
          `${isAllGenInfoFilled.emptyFields.join(", ")} fields are required`,
        );
      }

      if (generalInfo.email && !emailRegex.test(generalInfo.email)) {
        return handleOpenSnackBar("error", "Invalid customer email format");
      }

      if (!isBillingEmailSame && !emailRegex.test(generalInfo.billingEmail)) {
        return handleOpenSnackBar("error", "Invalid billing email format");
      }

      if (
        generalInfo?.isAddCustomer &&
        generalInfo?.secondName?.trim() === ""
      ) {
        return handleOpenSnackBar("error", "Add Customer field is required");
      }

      const isContractSeclected = contractLength.find(
        (item) => item.isSelected === true,
      );

      if (isContractSeclected === undefined) {
        return handleOpenSnackBar(
          "error",
          "Kindly select contract length first",
        );
      }

      if (hasErrorInLineValues(cliData) && !adminDetails?.canPreview) {
        return handleOpenSnackBar(
          "error",
          "Error in connection box. Please resolve.",
        );
      }

      const isAllowedToProceed = cliData.every((item) => {
        if (item?.digitalTariff?.isSelected) {
          return (
            item.transfer.isSelected ||
            item.migration.isSelected ||
            item.upgradeCharge.isSelected
          );
        }
        return true;
      });

      if (!isAllowedToProceed) {
        return handleOpenSnackBar(
          "error",
          "Kindly select at least one option (Install, Migration and Transfer) for Digital/NGN",
        );
      }

      if (isNaN(routerValue.price)) {
        return handleOpenSnackBar("error", "Router price can not be empty");
      }

      if (isNaN(cordedPhone.price)) {
        return handleOpenSnackBar(
          "error",
          "Corded phone price can not be empty",
        );
      }

      if (isNaN(cordedlessPhone.price)) {
        return handleOpenSnackBar(
          "error",
          "Cordless phone price can not be empty",
        );
      }

      if (!equipments.value?.trim() && isNaN(equipments.price)) {
        return handleOpenSnackBar(
          "error",
          "Kindly Select or write value For Equipments",
        );
      }

      if (equipments.value?.trim() && isNaN(equipments.price)) {
        return handleOpenSnackBar("error", "Equipment price can not be empty");
      }

      if (generalInfo.eBilling == "") {
        return handleOpenSnackBar(
          "error",
          "Kindly select option for paper billing Charges",
        );
      }

      if (generalInfo.eBilling == "No" && paperBillingPrice.error) {
        return handleOpenSnackBar(
          "error",
          "Kindly resolve error on paper billing charges",
        );
      }

      const paperBillingField = areAllKeysFilled(eBillingAddress);

      if (generalInfo.eBilling == "No" && !paperBillingField.allFilled) {
        return handleOpenSnackBar(
          "error",
          `${paperBillingField.emptyFields.join(
            ", ",
          )} fields are required for Paper Billing`,
        );
      }

      if (!isReqDebitDetail.yes && !isReqDebitDetail.no) {
        return handleOpenSnackBar(
          "error",
          "Kindly select option for debit details",
        );
      }

      // if (isReqDebitDetail.yes) {
      //   const debitDetailField = areAllKeysFilled(debitDetails);

      //   if (!debitDetailField.allFilled) {
      //     return handleOpenSnackBar(
      //       "error",
      //       `${debitDetailField.emptyFields.join(
      //         ", ",
      //       )} fields are required for Debit Details`,
      //     );
      //   }

      //   if (!/^\d{6}$/.test(debitDetails?.shortCode?.trim())) {
      //     return handleOpenSnackBar("error", "The sort code must be 6 digits.");
      //   }
      //   if (!/^\d{7,8}$/.test(debitDetails?.accountNumber?.trim())) {
      //     return handleOpenSnackBar(
      //       "error",
      //       "Account number must be 7 or 8 digits only.",
      //     );
      //   }

      //   if (!isBankDetailsVerified) {
      //     return handleOpenSnackBar("error", "Debit details is not verified");
      //   }
      // }

      if (!phoneValidation || phoneValidation?.IsValid === "No") {
        return handleOpenSnackBar("error", "Mobile Number is not verified");
      }

      if (totalGrossProfit <= 0 && !adminDetails?.canPreview) {
        return handleOpenSnackBar(
          "error",
          "Gross profit can not be negation or Zero",
        );
      }

      // Digital Services Validation
      const isAnyDigitalSelected = cliData.some(
        (item) => item.digitalTariff.isSelected,
      );

      if (
        isAnyDigitalSelected &&
        routerValue.value === 0 &&
        cordedPhone.value === 0 &&
        cordedlessPhone.value === 0
      ) {
        return handleDigitalServiceWarning(
          `You have selected digital tariff and have not selected router as well as any of the phones`,
          isPreview,
        );
      }

      if (isAnyDigitalSelected && routerValue.value === 0) {
        return handleDigitalServiceWarning(
          `You have selected digital tariff and you have not selected any router`,
          isPreview,
        );
      }
      if (
        isAnyDigitalSelected &&
        cordedPhone.value === 0 &&
        cordedlessPhone.value === 0
      ) {
        return handleDigitalServiceWarning(
          `You have selected digital tarrif and you have not selected any of the phones`,
          isPreview,
        );
      }

      if (isPreview) {
        navigate("/preview-contract", { state: { type: "add", id: null } });
      } else {
        await handleCreateContract();
      }
    } catch (error) {
      handleOpenSnackBar("error", "An unexpected error occurred");
      console.error("Contract handling error:", error);
    }
  };

  const handleDigitalServiceWarning = (message, isPreview) => {
    return Swal.fire({
      title: "Are you sure?",
      text: message,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Continue",
      cancelButtonText: "Go Back",
    }).then(async (result) => {
      if (result.isConfirmed) {
        if (isPreview) {
          navigate("/preview-contract", { state: { type: "add", id: null } });
        } else {
          await handleCreateContract();
        }
      }
    });
  };

  const handleCreateContract = async () => {
    const creatorId = isAdmin ? generalInfo?.agentId : userData?._id;

    try {
      let payload = {
        note,
        generalInfo,
        isBillingEmailSame,
        contractLength: selectedContractLength(contractLength),
        cliData,
        downloadSpeed,
        uploadSpeed,
        routerValue,
        cordedPhone,
        cordedlessPhone,
        equipments,
        paperBillingPrice,
        eBillingAddress,
        isDebitDetails: isReqDebitDetail.yes,
        debitDetails,
        totalRevenue,
        totalOneOfCost,
        totalGrossProfit: totalGrossProfit.toFixed(2),
        contractNote: allNotes,
        agentId: creatorId,
        contractEndDate,
        followUpDate,
        isUpfrontPaymentCollected,
        status: "envelope-not-sent",
        isBankDetailsVerified,
        phoneDetail: phoneValidation,
        createdBy: userData?.designation,
        ...(isAdmin && { contractSignedAt: signedDate || null }),
        ...(discountDetails && { discountDetails, discount }),
      };

      const response = await createContract(payload);

      if (response.data.status === "success") {
        navigate("/all-prospects");
        handleResetData(true);
        dispatch(setPhoneValidation(null));
        dispatch(setSignedDate(null));
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue =
        "Are you sure you want to reload? All your data will be lost!";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [contractLength]);

  return (
    <AppHeader>
      {/* <Stack alignItems={"center"} textAlign={"center"} spacing={0.5}>
        <Typography
          variant="primaryText"
          component={"h1"}
          fontSize={{ xs: "1.5rem", md: "2.125rem" }}
        >
          Add New Connection
        </Typography>
        <Typography variant="secondaryText" component={"p"} maxWidth={380}>
          Make sure all details are correct,based on client requirement
        </Typography>
      </Stack> */}

      {/* General Information */}

      <GeneralConnections />

      {/* Contract length */}
      <ContractLength />

      {selectedContractLength(contractLength) !== undefined && (
        <>
          {/* cli */}
          <Cli />

          {/* Dowload */}

          {isDownloadSpeed && <Download />}

          {/* upload */}
          {isUploadSpeed && <Upload />}

          {/* Routers */}
          <Routers />

          {/* Corded Phone*/}
          {/* {!legacySelectedCheck() && <CordedValues />} */}

          <CordedPhone />

          {/* Cordless Phone */}

          <CordedlessPhone />

          {/* Other Equipments */}
          <Equipments />

          {/* Discount */}
          <Discount />

          {/* Platform upgrade charge not using now because every box has own upgradecharge*/}

          {/* <PlatformUpgradeCharge /> */}

          {/*  Paper billing charges */}
          <PaperBillingCharges />

          {/* Same connections */}
          {generalInfo.eBilling && generalInfo.eBilling === "No" && (
            <SameConnections />
          )}

          {/* Debit details */}
          <DebitDetails type={"add"} />

          {/* Payment Done */}

          {/* Note  */}
          <Note contractDetails={null} type={"add"} fetchDetail={() => {}} />

          {/* Manaully set signed Date */}
          {isAdmin && <SignedDate />}

          {/* Button */}
          <Stack
            my={5}
            mb={8}
            gap={2}
            direction={"row"}
            alignItems={"center"}
            justifyContent={"flex-end"}
          >
            <GlobalButton
              variant={"custom_gradient"}
              title={"Save Contract"}
              handleClickBtn={() => handlePreviewContrat(false)}
              style={{
                width: { xs: "100%", sm: 260 },
                fontSize: "1rem",
                borderRadius: "100px",
              }}
            />
            <GlobalButton
              variant={"custom_gradient"}
              title={"Preview Contract"}
              handleClickBtn={() => handlePreviewContrat(true)}
              style={{
                width: { xs: "100%", sm: 260 },
                fontSize: "1rem",
                borderRadius: "100px",
              }}
            />
          </Stack>

          {/* fixed bottom tab to show profits */}
          <Stack
            bgcolor={"primary.main"}
            px={2}
            py={1}
            position={"fixed"}
            bottom={0}
            left={0}
            right={0}
            direction={{ xs: "column", sm: "row" }}
            justifyContent={"flex-end"}
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={{ sm: 2 }}
          >
            <Typography
              fontSize={{ xs: "1.12rem", sm: "1.225rem" }}
              color={"secondary.light"}
            >
              Revenue: £{totalRevenue}
            </Typography>
            <Typography
              fontSize={{ xs: "1.12rem", sm: "1.225rem" }}
              color={"secondary.light"}
            >
              One-off Cost: £{totalOneOfCost}
            </Typography>
            <Typography
              fontSize={{ xs: "1.12rem", sm: "1.225rem" }}
              color={"secondary.light"}
            >
              Gross Profit: £{totalGrossProfit.toFixed(2)}
            </Typography>
          </Stack>
        </>
      )}
    </AppHeader>
  );
};

export default AddNewConnectionsPage;
